import { FieldText, FormField } from '@dixa/lib-shared-habla';
import { ReactElement, useCallback } from 'react';
import { Field } from 'redux-form';

import { FieldTemplate } from 'components/fieldTemplate';
import { CARD_V2 } from 'components/shared/util/customCard';
import { useAppSelector } from 'utils/redux/useAppSelector';

import type { CardState, FormMode } from '../cards.types';
import { editorMaxLines, templateHelpText } from '../cards.utils';
import { CardsFormWrapper } from '../components/formWrapper';

import salesforceTemplate from './fixtures/salesforceTemplate.liquid.html';

interface SalesforceCardsProps {
  mode: FormMode;
}

export function SalesforceCards({ mode }: SalesforceCardsProps): ReactElement {
  const { organizationId } = useAppSelector(({ settings }) => {
    return { organizationId: settings.organization.organization.id };
  });

  const salesforceCardType = 'salesforceusernamepasswordflow';

  const templateURL = useCallback(
    (formValues) =>
      `https://proxy.dixa/v1/proxy/${organizationId}/${salesforceCardType}?consumer_key=${encodeURIComponent(
        formValues.hook.consumer_key,
      )}&consumer_secret=${encodeURIComponent(
        formValues.hook.consumer_secret,
      )}&security_token=${encodeURIComponent(
        formValues.hook.security_token,
      )}&account_email=${encodeURIComponent(
        formValues.hook.account_email,
      )}&account_password=${encodeURIComponent(
        formValues.hook.account_password,
      )}&a_email={{a_email}}&a_phone_number={{a_phone_number}}`,
    [organizationId],
  );

  const getEditObject = useCallback(
    (formValues: CardState) =>
      ({
        card_type: salesforceCardType,
        hook: {
          hook_id: formValues.hook.hook_id,
          title: formValues.hook.title,
          uri_template: templateURL(formValues),
          headers: {},
          card_version: formValues.hook.card_version,
        },
        template: formValues.template,
      } as CardState),
    [templateURL],
  );

  const getCreateObject = useCallback(
    (formValues: CardState) =>
      ({
        card_type: salesforceCardType,
        hook: {
          title: formValues.hook.title,
          uri_template: templateURL(formValues),
          headers: {},
          card_version: CARD_V2,
        },
        template: formValues.template,
      } as CardState),
    [templateURL],
  );

  return (
    <CardsFormWrapper
      form="salesforceCardsForm"
      mode={mode}
      cardType="salesforce"
      getEditObject={getEditObject}
      getCreateObject={getCreateObject}
      template={salesforceTemplate}
    >
      <>
        <FormField span={3}>
          <Field
            required
            label="Title"
            name="hook.title"
            component={FieldText}
            placeholder="Insert title"
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Consumer key"
            name="hook.consumer_key"
            component={FieldText}
            placeholder="Insert consumer key"
            helpIcon={{
              title: 'Consumer key help',
              text: 'Please consult the guide for information about the consumer key.',
            }}
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Consumer secret"
            name="hook.consumer_secret"
            component={FieldText}
            placeholder="Insert consumer secret"
            helpIcon={{
              title: 'Consumer secret help',
              text: 'Please consult the guide for information about the consumer secret.',
            }}
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Security token"
            name="hook.security_token"
            component={FieldText}
            placeholder="Insert security token"
            helpIcon={{
              title: 'Security token help',
              text: 'Please consult the guide for information about the security token.',
            }}
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Account email"
            name="hook.account_email"
            component={FieldText}
            placeholder="Account email"
            helpIcon={{
              title: 'Account email help',
              text: 'Please consult the guide for information about the account email.',
            }}
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Account password"
            name="hook.account_password"
            component={FieldText}
            placeholder="Insert account password"
            helpIcon={{
              title: 'Account password help',
              text: 'Please consult the guide for information about the account password.',
            }}
          />
        </FormField>
        <FormField>
          <Field
            required
            defaultHidden
            label="Template content"
            name="template"
            component={FieldTemplate}
            helpIcon={{
              title: 'Template help',
              text: templateHelpText,
              requireClick: true,
            }}
            editorOptions={{ maxLines: editorMaxLines }}
            placeholder="Insert template"
          />
        </FormField>
      </>
    </CardsFormWrapper>
  );
}
